import { useMemo, type ReactNode } from 'react';
import { MdCheck } from 'react-icons/md';
import { cn } from '../../lib/utils';
import { useTheme } from '../../context/ThemeContext';
import {
  PROCUREMENT_WORKFLOW_STEPS,
  getActiveStepDefinition,
  getNextWorkflowStep,
  getProcurementWorkflowStepIndex,
  getStepStatusLabel,
  getStepVisualState,
  getWorkflowProgressPercent,
  getWorkflowStageProgressPercent,
  normalizeProcurementWorkflowStatus,
  resolveProcurementWorkflowStatus,
  type ProcurementWorkflowContext,
  type ProcurementWorkflowStatus,
  type ProcurementWorkflowStepDefinition,
  type StepVisualState,
} from './procurement-workflow.utils';

export interface ProcurementWorkflowStepperProps {
  /** Preferred: explicit current step from API. */
  currentStatus?: ProcurementWorkflowStatus | string | null;
  /** Optional context to derive step when `currentStatus` is absent. */
  context?: ProcurementWorkflowContext;
  /** Optional timestamp for summary panel (e.g. RFQ updatedAt). */
  lastUpdated?: string | Date | null;
  className?: string;
}

type SummaryAccent = 'blue' | 'cyan' | 'indigo' | 'green';

type SummaryAccentStyles = {
  border: string;
  hoverGlow: string;
  label: string;
  value?: string;
};

function getSummaryAccentStyles(accent: SummaryAccent, isDark: boolean): SummaryAccentStyles {
  const accents: Record<SummaryAccent, { light: SummaryAccentStyles; dark: SummaryAccentStyles }> = {
    blue: {
      light: {
        border: 'border-l-[#0088cc]',
        hoverGlow: 'hover:shadow-[inset_3px_0_0_#0088cc,0_2px_12px_rgba(0,136,204,0.06)]',
        label: 'text-[hsl(var(--muted-foreground))]',
      },
      dark: {
        border: 'border-l-[#0088cc]',
        hoverGlow: 'hover:shadow-[inset_0_0_24px_rgba(0,136,204,0.12)]',
        label: 'text-[#0088cc]/70',
      },
    },
    cyan: {
      light: {
        border: 'border-l-cyan-500',
        hoverGlow: 'hover:shadow-[inset_3px_0_0_#06b6d4,0_2px_12px_rgba(6,182,212,0.06)]',
        label: 'text-[hsl(var(--muted-foreground))]',
        value: 'text-cyan-600',
      },
      dark: {
        border: 'border-l-cyan-400',
        hoverGlow: 'hover:shadow-[inset_0_0_24px_rgba(34,211,238,0.12)]',
        label: 'text-cyan-400/80',
        value: 'text-cyan-300',
      },
    },
    indigo: {
      light: {
        border: 'border-l-indigo-500',
        hoverGlow: 'hover:shadow-[inset_3px_0_0_#6366f1,0_2px_12px_rgba(99,102,241,0.06)]',
        label: 'text-[hsl(var(--muted-foreground))]',
      },
      dark: {
        border: 'border-l-indigo-400',
        hoverGlow: 'hover:shadow-[inset_0_0_24px_rgba(129,140,248,0.12)]',
        label: 'text-indigo-400/80',
      },
    },
    green: {
      light: {
        border: 'border-l-emerald-500',
        hoverGlow: 'hover:shadow-[inset_3px_0_0_#10b981,0_2px_12px_rgba(16,185,129,0.06)]',
        label: 'text-[hsl(var(--muted-foreground))]',
      },
      dark: {
        border: 'border-l-emerald-400',
        hoverGlow: 'hover:shadow-[inset_0_0_24px_rgba(52,211,153,0.12)]',
        label: 'text-emerald-400/80',
      },
    },
  };

  return isDark ? accents[accent].dark : accents[accent].light;
}

function formatLastUpdated(value?: string | Date | null): string {
  if (!value) return '—';
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function StatusBadge({ state, isDark }: { state: StepVisualState; isDark: boolean }) {
  const label = getStepStatusLabel(state);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border',
        state === 'active' &&
          (isDark
            ? 'bg-[#0088cc]/20 text-[#38bdf8] border-[#0088cc]/30'
            : 'bg-[#0088cc]/10 text-[#0088cc] border-[#0088cc]/25'),
        state === 'completed' &&
          (isDark
            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
            : 'bg-emerald-50 text-emerald-700 border-emerald-200'),
        state === 'pending' &&
          (isDark
            ? 'bg-slate-800/80 text-[#0088cc]/65 border-slate-700'
            : 'bg-[#0088cc]/10 text-[hsl(var(--muted-foreground))] border-[#0088cc]/20')
      )}
    >
      {label}
    </span>
  );
}

function StepCircle({
  state,
  icon: Icon,
  title,
  isDark,
}: {
  state: StepVisualState;
  icon: ProcurementWorkflowStepDefinition['icon'];
  title: string;
  isDark: boolean;
}) {
  const isActive = state === 'active';
  const isCompleted = state === 'completed';

  return (
    <div
      className={cn(
        'relative z-10 flex items-center justify-center rounded-full transition-all duration-300 ease-out',
        'group-hover:scale-[1.03]',
        isActive && 'h-16 w-16',
        !isActive && 'h-12 w-12 sm:h-14 sm:w-14'
      )}
      aria-current={isActive ? 'step' : undefined}
    >
      {isActive ? (
        <>
          <span
            className="absolute inset-0 rounded-full bg-[#0088cc]/25 animate-ping"
            aria-hidden
          />
          <span
            className="absolute -inset-1 rounded-full bg-[#0088cc]/15 blur-md"
            aria-hidden
          />
        </>
      ) : null}

      <div
        className={cn(
          'relative flex h-full w-full items-center justify-center rounded-full border-2 transition-all duration-300',
          isCompleted &&
            'border-[#0077b3] bg-gradient-to-br from-[#0088cc] to-[#006699] text-white shadow-md shadow-[#0088cc]/30',
          isActive &&
            cn(
              'border-[#0088cc] shadow-lg shadow-[#0088cc]/25 ring-4 ring-[#0088cc]/20',
              isDark
                ? 'bg-[#0f172a] text-[#38bdf8] ring-[#0088cc]/25'
                : 'bg-white text-[#0088cc]'
            ),
          state === 'pending' &&
            cn(
              isDark
                ? 'border-slate-700 bg-slate-800/90 text-[hsl(var(--muted-foreground))] group-hover:border-slate-600 group-hover:bg-slate-800'
                : 'border-[#0088cc]/20 bg-[#0088cc]/10 text-[#0088cc]/65 group-hover:border-[#0088cc]/25 group-hover:bg-[#0088cc]/5'
            )
        )}
      >
        {isCompleted ? (
          <MdCheck className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden />
        ) : (
          <Icon className={cn('h-5 w-5', isActive && 'h-6 w-6')} aria-hidden />
        )}
      </div>
      <span className="sr-only">{title}</span>
    </div>
  );
}

function ProgressTrack({
  progressPercent,
  className,
  isDark,
}: {
  progressPercent: number;
  className?: string;
  isDark: boolean;
}) {
  return (
    <div
      className={cn(
        'h-[5px] overflow-hidden rounded-full',
        isDark ? 'bg-slate-700/80' : 'bg-[#0088cc]/15',
        className
      )}
      aria-hidden
    >
      <div
        className="h-full rounded-full bg-gradient-to-r from-[#0088cc] via-[#0077b3] to-[#006699] transition-[width] duration-700 ease-out shadow-[0_0_8px_rgba(0,136,204,0.35)]"
        style={{ width: `${progressPercent}%` }}
      />
    </div>
  );
}

function WorkflowStepItem({
  step,
  index,
  activeIndex,
  isDark,
  compact = false,
}: {
  step: ProcurementWorkflowStepDefinition;
  index: number;
  activeIndex: number;
  isDark: boolean;
  compact?: boolean;
}) {
  const state = getStepVisualState(index, activeIndex);

  return (
    <li
      className={cn(
        'group flex flex-col items-center text-center',
        compact ? 'w-[5.75rem] shrink-0 snap-center sm:w-[6.5rem]' : 'min-w-0'
      )}
    >
      <StepCircle state={state} icon={step.icon} title={step.title} isDark={isDark} />
      <p
        className={cn(
          'font-bold uppercase tracking-wide transition-colors duration-200',
          compact ? 'mt-2.5 text-[11px] leading-tight' : 'mt-3.5 text-sm',
          state === 'active' && (isDark ? 'text-[#38bdf8]' : 'text-[#0088cc]'),
          state === 'completed' && (isDark ? 'text-slate-100' : 'text-[hsl(var(--foreground))]'),
          state === 'pending' &&
            (isDark
              ? 'text-[#0088cc]/65 group-hover:text-[#0088cc]/45'
              : 'text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--muted-foreground))]')
        )}
      >
        {compact ? step.shortTitle : step.title}
      </p>
      <div className={cn(compact ? 'mt-1.5' : 'mt-2')}>
        <StatusBadge state={state} isDark={isDark} />
      </div>
    </li>
  );
}

function SummarySegment({
  accent,
  label,
  children,
  isDark,
  className,
}: {
  accent: SummaryAccent;
  label: string;
  children: ReactNode;
  isDark: boolean;
  className?: string;
}) {
  const styles = getSummaryAccentStyles(accent, isDark);

  return (
    <div
      className={cn(
        'group relative min-w-0 border-l-[3px] px-4 py-3.5 transition-all duration-300',
        isDark
          ? 'bg-[#0f172a] hover:bg-[#1e293b]/40'
          : 'bg-white hover:bg-[#0088cc]/5/90',
        styles.border,
        styles.hoverGlow,
        className
      )}
    >
      <p className={cn('text-[10px] font-bold uppercase tracking-[0.14em]', styles.label)}>
        {label}
      </p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function WorkflowSummary({
  activeStep,
  nextStep,
  stageProgressPercent,
  timelineProgressPercent,
  lastUpdated,
  isDark,
}: {
  activeStep: ProcurementWorkflowStepDefinition;
  nextStep: ProcurementWorkflowStepDefinition | null;
  stageProgressPercent: number;
  timelineProgressPercent: number;
  lastUpdated: string;
  isDark: boolean;
}) {
  const progressStyles = getSummaryAccentStyles('cyan', isDark);
  const valueClass = isDark
    ? 'text-sm font-extrabold text-white'
    : 'text-sm font-extrabold text-[hsl(var(--foreground))]';
  const mutedValueClass = isDark
    ? 'truncate text-sm font-semibold text-slate-200'
    : 'truncate text-sm font-semibold text-[hsl(var(--foreground))]';

  return (
    <div
      className={cn(
        'mt-5 overflow-hidden rounded-xl border',
        isDark
          ? 'border-[#0088cc]/15 bg-[#0f172a] shadow-[inset_0_1px_0_rgba(0,136,204,0.08),0_4px_24px_rgba(0,0,0,0.25)]'
          : 'border-[#0088cc]/20 bg-white shadow-sm'
      )}
    >
      <div
        className={cn(
          'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
          isDark
            ? 'divide-y divide-[#0088cc]/10 lg:divide-x lg:divide-y-0'
            : 'divide-y divide-[#e2e8f0] lg:divide-x lg:divide-y-0'
        )}
      >
        <SummarySegment accent="blue" label="Current Stage" isDark={isDark}>
          <p className={cn(valueClass, 'truncate')}>{activeStep.title}</p>
        </SummarySegment>

        <SummarySegment accent="cyan" label="Progress" isDark={isDark}>
          <p className={cn('text-sm font-extrabold', progressStyles.value ?? valueClass)}>
            {stageProgressPercent}%
          </p>
          <div className="mt-2 pr-1">
            <ProgressTrack progressPercent={timelineProgressPercent} isDark={isDark} />
          </div>
        </SummarySegment>

        <SummarySegment accent="indigo" label="Next Stage" isDark={isDark}>
          <p className={cn(valueClass, 'truncate')}>
            {nextStep ? nextStep.title : 'Workflow complete'}
          </p>
        </SummarySegment>

        <SummarySegment accent="green" label="Last Updated" isDark={isDark}>
          <p className={mutedValueClass}>{lastUpdated}</p>
        </SummarySegment>
      </div>
    </div>
  );
}

export function ProcurementWorkflowStepper({
  currentStatus,
  context,
  lastUpdated,
  className,
}: ProcurementWorkflowStepperProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const activeStatus = useMemo(() => {
    const normalized = normalizeProcurementWorkflowStatus(currentStatus ?? null);
    if (normalized) return normalized;
    if (context) return resolveProcurementWorkflowStatus(context);
    return 'RFQ' as ProcurementWorkflowStatus;
  }, [currentStatus, context]);

  const activeIndex = getProcurementWorkflowStepIndex(activeStatus);
  const activeStep = getActiveStepDefinition(activeStatus);
  const nextStep = getNextWorkflowStep(activeIndex);
  const timelineProgressPercent = getWorkflowProgressPercent(
    activeIndex,
    PROCUREMENT_WORKFLOW_STEPS.length
  );
  const stageProgressPercent = getWorkflowStageProgressPercent(
    activeIndex,
    PROCUREMENT_WORKFLOW_STEPS.length
  );
  const formattedLastUpdated = formatLastUpdated(lastUpdated);

  return (
    <section
      className={cn(
        'relative w-full overflow-hidden rounded-2xl border',
        'before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:bg-gradient-to-r before:from-[#0088cc] before:via-[#0077b3] before:to-[#006699]',
        isDark
          ? 'border-[#0088cc]/15 bg-[#111827] shadow-[0_4px_32px_rgba(0,0,0,0.35)]'
          : 'border-[#0088cc]/20 bg-white shadow-sm',
        className
      )}
      aria-label="Procurement workflow progress"
      data-theme={resolvedTheme}
    >
      <div className="px-4 py-4 sm:px-6 sm:py-5 lg:px-7">
        {/* Header */}
        <div
          className={cn(
            'flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-center sm:justify-between',
            isDark ? 'border-[#0088cc]/10' : 'border-[#0088cc]/20'
          )}
        >
          <div className="min-w-0">
            <h3
              className={cn(
                'text-lg font-extrabold tracking-tight sm:text-xl',
                isDark ? 'text-white' : 'text-[hsl(var(--foreground))]'
              )}
            >
              Procurement Workflow
            </h3>
            <p
              className={cn(
                'mt-0.5 text-xs font-medium sm:text-sm',
                isDark ? 'text-[#0088cc]/65' : 'text-[hsl(var(--muted-foreground))]'
              )}
            >
              Track directive lifecycle from RFQ to Claim Resolution
            </p>
          </div>
          <span
            className={cn(
              'inline-flex w-fit shrink-0 items-center rounded-full border px-3.5 py-1.5 text-xs font-bold',
              isDark
                ? 'border-[#0088cc]/30 bg-[#0088cc]/15 text-[#38bdf8] shadow-[0_0_12px_rgba(0,136,204,0.15)]'
                : 'border-[#0088cc]/20 bg-[#0088cc]/10 text-[#0088cc] shadow-sm'
            )}
          >
            Step {activeIndex + 1} of {PROCUREMENT_WORKFLOW_STEPS.length}
          </span>
        </div>

        {/* Desktop timeline */}
        <div className="relative mt-5 hidden md:block">
          <div className="absolute left-[8.333%] right-[8.333%] top-8 -translate-y-1/2">
            <ProgressTrack progressPercent={timelineProgressPercent} isDark={isDark} />
          </div>

          <ol className="relative grid grid-cols-6 gap-1 lg:gap-2">
            {PROCUREMENT_WORKFLOW_STEPS.map((step, index) => (
              <WorkflowStepItem
                key={step.id}
                step={step}
                index={index}
                activeIndex={activeIndex}
                isDark={isDark}
              />
            ))}
          </ol>
        </div>

        {/* Mobile timeline */}
        <div className="relative mt-4 md:hidden">
          <div className="mb-3 px-2">
            <ProgressTrack progressPercent={timelineProgressPercent} isDark={isDark} />
          </div>

          <ol className="flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 pl-1 pr-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {PROCUREMENT_WORKFLOW_STEPS.map((step, index) => (
              <WorkflowStepItem
                key={step.id}
                step={step}
                index={index}
                activeIndex={activeIndex}
                isDark={isDark}
                compact
              />
            ))}
          </ol>
        </div>

        <WorkflowSummary
          activeStep={activeStep}
          nextStep={nextStep}
          stageProgressPercent={stageProgressPercent}
          timelineProgressPercent={timelineProgressPercent}
          lastUpdated={formattedLastUpdated}
          isDark={isDark}
        />
      </div>
    </section>
  );
}

export default ProcurementWorkflowStepper;
